import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router';
import { SimInventory } from 'simInventory/SimInventory';
import { SimProfileDetailsTab } from 'src/simInventory/SimInventoryDetails/SimProfileDetailsTab';
import { RSimInventory } from 'rSimInventory/RSimInventory';
import { BulkProvisioningJob } from '@/activityLog/bulkProvisioning/BulkProvisioningJob';
import { Routes } from './Routes.constants';
import { SessionHistoryWithInterims } from 'simInventory/SessionHistory/SessionHistoryWithInterims';
import { HistoryAnalysis } from 'simInventory/SessionHistory/HistoryAnalysis/HistoryAnalysis';
import { RSimHistoryAnalysis } from 'rSimInventory/HistoryAnalysis/RSimHistoryAnalysis';
import { RSimSessionHistoryEntry } from './rSimInventory/Components/RSimSessionHistory/RSimSessionHistoryEntry';

import { CustomFieldsPage } from './admin/customFields/CustomFieldsPage';
import { RSimManufacturingFilePage } from './admin/rSimManufacturingFile/RSimManufacturingFilePage';
import { SubscriptionStatusReconciliationPage } from './admin/subscriptionStatusReconciliation/SubscriptionStatusReconciliationPage';
import { useSubscriptionStatusReconciliationAvailable } from './admin/subscriptionStatusReconciliation/hooks/useSubscriptionStatusReconciliationAvailable';
import { useHasFeatureFlag } from './featureFlags/useHasFeatureFlag';
import { SimProducts } from './products/sim/SimProducts';
import { ExportUnavailablePage } from 'common/pages/ExportUnavailablePage';
import { SimProductDetails } from 'products/sim/Details/SimProductDetails';
import { RSimInventoryEntry } from './rSimInventory/Details/RSimInventoryEntry';
import { RouteErrorBoundary } from './auth/RouteErrorBoundary';
import { useDefaultRoutingPath } from './routing/hooks/useDefaultRoutingPath';
import { TariffSummaryPage } from './products/tariffSummary/TariffSummaryPage';
import { DashboardPage } from './dashboard/DashboardPage';
import { useTariffSummaryAvailable } from 'products/tariffSummary/hooks/useTariffSummaryAvailable';
import { useProductSummaryAvailable } from 'products/productsSummary/hooks/useProductSummaryAvailable';
import { HydrateAtoms } from './atoms/components/HydrateAtoms';
import { activityLogNavigationAtom, productNavigationAtom } from './atoms/pages/pages';
import { useDefaultProductTabRoute } from './products/hooks/useDefaultProductTabRoute';
import { ProductSummaryPage } from 'products/productsSummary/ProductSummaryPage';

import { RootLayout } from './common/components/AppLayout/RootLayout';

import { ProductPageLayout } from './products/layout/ProductPageLayout';
import { RSimDetailsPageLayout } from './rSimInventory/Details/layout/RSimDetailsPageLayout';
import { SimProductDetailsPageLayout } from './products/sim/Details/layout/SimProductDetailsPageLayout';
import { SimProfilesDetailsPageLayout } from './simInventory/SimInventoryDetails/layout/SimProfilesDetailsPageLayout';
import { AdminPageLayout } from './admin/layout/AdminPageLayout';
import { useAuditTrailAvailable } from './auditTrail/hooks/useAuditTrailAvailable';
import { BulkProvisioning } from '@/activityLog/bulkProvisioning/BulkProvisioning';
import { useSimProfileDetailsAvaibility } from './simInventory/SimInventoryDetails/hooks/useSimProfileDetailsAvaibility';
import { useCustomFieldsAvailable } from './admin/customFields/hooks/useCustomFieldsAvailable';
import { useRSimManufacturingFileAvailable } from './admin/rSimManufacturingFile/hooks/useRSimManufacturingFileAvailable';
import { useAdminAvailable } from './admin/hooks/useAdminAvailable';
import { AuditTrailPage } from './auditTrail/AuditTrailPage';
import { ESimInventory } from './products/esim/ESimInventory';
import { ESimDetails } from './products/esim/Details/ESimDetails';
import { ESimDetailsPageLayout } from './products/esim/Details/layout/ESimDetailsPageLayout';
import { LocationsPage } from 'src/locations/LocationsPage';
import { ProductLocationDetails } from 'src/locations/ProductLocationDetails';
import { ActivityLogPageLayout } from '@/activityLog/layout/ActivityLogPageLayout.tsx';
import { SimProvisioning } from '@/activityLog/bulkProvisioning/SimProvisoning.tsx';
import { ProductManagement } from '@/activityLog/productsManagement/ProductManagement.tsx';
import { useProductsManagementAvailable } from '@/activityLog/productsManagement/hooks/useProductsManagementAvailable.ts';
import { useSetAtom } from 'jotai';

export const AppRoutes = () => {
  const locationsEnabled = useHasFeatureFlag('Locations');
  const productManagementEnabled = useHasFeatureFlag('ProductManagement');

  const defaultPageRouting = useDefaultRoutingPath();
  const defaultProductTabRoute = useDefaultProductTabRoute();

  const tariffSummaryViewEnabled = useTariffSummaryAvailable();
  const productSummaryViewEnabled = useProductSummaryAvailable();
  const auditTrailAvailable = useAuditTrailAvailable();
  const simProfileDetailsAvailable = useSimProfileDetailsAvaibility();
  const customFieldsAvailable = useCustomFieldsAvailable();
  const rSimManufacturingFileAvailable = useRSimManufacturingFileAvailable();
  const reconciliationAvailable = useSubscriptionStatusReconciliationAvailable();
  const adminAvailable = useAdminAvailable();

  const hasProductManagementPermissions = useProductsManagementAvailable();
  const setActivityLogRoute = useSetAtom(activityLogNavigationAtom);
  if (!hasProductManagementPermissions) {
    setActivityLogRoute(Routes.simProvisioning);
  }

  const routes = createRoutesFromElements(
    <Route errorElement={<RouteErrorBoundary />}>
      <Route
        element={
          <RootLayout>
            <Outlet />
          </RootLayout>
        }
      >
        <Route path={Routes.dashboard} element={<DashboardPage />} />
        <Route
          element={
            <ProductPageLayout>
              <Outlet />
            </ProductPageLayout>
          }
        >
          {tariffSummaryViewEnabled && (
            <Route path={Routes.tariffSummary} element={<TariffSummaryPage />} />
          )}
          {productSummaryViewEnabled && (
            <Route path={Routes.productSummary} element={<ProductSummaryPage />} />
          )}
          <Route path={Routes.productsSim} element={<SimProducts />} />

          <Route path={Routes.productsRsim} element={<RSimInventory />} />

          <Route path={Routes.esim} element={<ESimInventory />} />
        </Route>

        <Route
          element={
            <RSimDetailsPageLayout>
              <Outlet />
            </RSimDetailsPageLayout>
          }
        >
          <Route path={`${Routes.productsRsim}/:euicc`} element={<RSimInventoryEntry />} />
          {locationsEnabled ? (
            <Route
              path={`${Routes.productsRsim}/:euicc/location`}
              element={<ProductLocationDetails />}
            />
          ) : null}
          <Route
            path={`${Routes.productsRsim}/:euicc/session-history`}
            element={<RSimSessionHistoryEntry />}
          />
          <Route
            path={`${Routes.productsRsim}/:euicc/history-analysis`}
            element={<RSimHistoryAnalysis />}
          />
        </Route>

        <Route
          element={
            <SimProductDetailsPageLayout>
              <Outlet />
            </SimProductDetailsPageLayout>
          }
        >
          <Route path={`${Routes.productsSim}/:iccid`} element={<SimProductDetails />} />
          <Route
            path={`${Routes.productsSim}/:iccid/history`}
            element={<SessionHistoryWithInterims />}
          />
          {locationsEnabled ? (
            <Route
              path={`${Routes.productsSim}/:iccid/location`}
              element={<ProductLocationDetails />}
            />
          ) : null}
          <Route
            path={`${Routes.productsSim}/:iccid/history-analysis`}
            element={<HistoryAnalysis />}
          />
          {auditTrailAvailable && (
            <Route element={<AuditTrailPage />} path={`${Routes.productsSim}/:iccid/audit-trail`} />
          )}
        </Route>
        <Route path={Routes.simInventory} element={<SimInventory />} />
        {simProfileDetailsAvailable && (
          <Route
            element={
              <SimProfilesDetailsPageLayout>
                <Outlet />
              </SimProfilesDetailsPageLayout>
            }
          >
            <Route path={`${Routes.simInventory}/:iccid`} element={<SimProfileDetailsTab />} />
            {locationsEnabled ? (
              <Route
                path={`${Routes.simInventory}/:iccid/location`}
                element={<ProductLocationDetails />}
              />
            ) : null}
            <Route
              path={`${Routes.simInventory}/:iccid/history`}
              element={<SessionHistoryWithInterims />}
            />
            <Route
              path={`${Routes.simInventory}/:iccid/history-analysis`}
              element={<HistoryAnalysis />}
            />
            {auditTrailAvailable && (
              <Route
                element={<AuditTrailPage />}
                path={`${Routes.simInventory}/:iccid/audit-trail`}
              />
            )}
          </Route>
        )}

        <Route
          element={
            <ESimDetailsPageLayout>
              <Outlet />
            </ESimDetailsPageLayout>
          }
        >
          <Route path={`${Routes.esim}/:eid`} element={<ESimDetails />} />
        </Route>

        {locationsEnabled ? <Route path={Routes.locations} element={<LocationsPage />} /> : null}

        {productManagementEnabled ? null : (
          <Route path={Routes.provisioningRequest} element={<BulkProvisioning />}>
            <Route path=":id" element={<BulkProvisioningJob />} />
          </Route>
        )}

        {adminAvailable && (
          <Route
            element={
              <AdminPageLayout>
                <Outlet />
              </AdminPageLayout>
            }
          >
            {customFieldsAvailable && (
              <Route path={`${Routes.admin}/custom-fields`} element={<CustomFieldsPage />} />
            )}

            {rSimManufacturingFileAvailable && (
              <Route
                path={`${Routes.admin}/rsim-manufacturing-file`}
                element={<RSimManufacturingFilePage />}
              />
            )}
            {reconciliationAvailable && (
              <Route
                path={Routes.subscriptionStatusReconciliation}
                element={<SubscriptionStatusReconciliationPage />}
              />
            )}
          </Route>
        )}
        {productManagementEnabled ? (
          <Route
            element={
              <ActivityLogPageLayout>
                <Outlet />
              </ActivityLogPageLayout>
            }
          >
            <Route
              path={`${Routes.productsManagement}`}
              element={
                hasProductManagementPermissions ? (
                  <ProductManagement />
                ) : (
                  <Navigate to={`${Routes.simProvisioning}`} />
                )
              }
            />
            <Route path={`${Routes.simProvisioning}`} element={<SimProvisioning />}>
              <Route path=":id" element={<BulkProvisioningJob />} />
            </Route>
          </Route>
        ) : null}
      </Route>
      <Route path="/export-failed" element={<ExportUnavailablePage />} />
      <Route
        path={Routes.products}
        element={
          <Navigate
            to={tariffSummaryViewEnabled ? Routes.tariffSummary : Routes.productSummary}
            replace
          />
        }
      />
      {productManagementEnabled ? (
        <Route
          path={Routes.activityLog}
          element={<Navigate to={Routes.productsManagement} replace />}
        />
      ) : null}
      {adminAvailable && (
        <Route path={Routes.admin} element={<Navigate to={Routes.customFields} replace />} />
      )}

      <Route path="*" element={<Navigate to={defaultPageRouting} replace />} />
    </Route>,
  );

  const router = createBrowserRouter(routes);

  return (
    <HydrateAtoms initialValues={[[productNavigationAtom, defaultProductTabRoute]]}>
      <RouterProvider router={router} />
    </HydrateAtoms>
  );
};
